import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { listCandidates, candidateStats, talentSearch } from "@/lib/api";
import {
  CandidateWorkspace, Stars, candidateStatusColor as statusColor,
} from "@/components/admin/CandidateWorkspace";
import { CandidateIntakeModal } from "@/components/admin/CandidateIntakeModal";
import { hasPerm } from "@/lib/auth";
import { Loader2, Users, Search, UserPlus } from "lucide-react";

const STATUSES = ["Applied", "Reviewing", "Qualified", "Interviewing", "Available", "Presented", "Placed", "Rejected", "Archived"];
const INDUSTRIES = ["Healthcare", "Legal", "Construction", "Property Management", "Finance", "Insurance", "Technology", "Retail", "Hospitality", "Manufacturing", "Other"];
const AVAILABILITY = ["Immediate", "2 Weeks", "30 Days", "Currently Employed", "Not Looking"];

export const CandidatesManager = ({ token, openCandidateId, openIntakeOnMount }) => {
  const canManage = hasPerm("candidates.manage");
  const [view, setView] = useState("all"); // all | search
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", status: "all", industry: "all", availability: "all" });

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [openId, setOpenId] = useState(null);
  const [showIntake, setShowIntake] = useState(!!openIntakeOnMount);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        candidateStats(token),
        listCandidates(token, filters),
      ]);
      setStats(s);
      setRows(l.candidates);
    } catch {
      toast.error("Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { load(); }, [load]);

  const runSearch = async () => {
    setSearching(true);
    try {
      const res = await talentSearch(token, { query: searchQuery });
      setSearchResults(res.candidates);
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // Allow the dashboard / global search to deep-link straight into a workspace.
  useEffect(() => { if (openCandidateId) setOpenId(openCandidateId); }, [openCandidateId]);

  const StatCard = ({ label, value }) => (
    <div className="rounded-xl border border-white/10 p-4" style={{ backgroundColor: "#0F1424" }}>
      <div className="text-white/50 text-xs">{label}</div>
      <div className="text-2xl font-bold text-white tabular-nums mt-1">{value ?? "\u2014"}</div>
    </div>
  );

  const list = view === "search" ? searchResults : rows;

  return (
    <div data-testid="candidates-manager">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users size={18} className="text-[#66B2FF]" /> Candidates
          </h2>
          <p className="text-white/50 text-sm">Rendeavored Talent Network</p>
        </div>
        <div className="sm:ml-auto flex items-center gap-2">
          <div className="flex gap-2 bg-[#0F1424] border border-white/10 rounded-xl p-1">
            <button onClick={() => setView("all")} data-testid="candidates-view-all"
              className={`px-4 py-1.5 rounded-lg text-sm ${view === "all" ? "bg-[#1E6BFF] text-white" : "text-white/60"}`}>All Candidates</button>
            <button onClick={() => setView("search")} data-testid="candidates-view-search"
              className={`px-4 py-1.5 rounded-lg text-sm ${view === "search" ? "bg-[#1E6BFF] text-white" : "text-white/60"}`}>Talent Search</button>
          </div>
          {canManage && (
            <Button onClick={() => setShowIntake(true)} data-testid="candidates-new-button"
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white h-10">
              <UserPlus size={16} className="mr-1.5" /> New Candidate
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={stats?.total} />
        <StatCard label="New This Week" value={stats?.new_this_week} />
        <StatCard label="Available" value={stats?.available} />
        <StatCard label="Interviewing" value={stats?.interviewing} />
        <StatCard label="Placed" value={stats?.placed} />
      </div>

      {/* Filters / Search */}
      {view === "all" ? (
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input data-testid="candidates-search" value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search name, email, skill, position"
              className="bg-[#0F1424] border-white/10 h-11 text-white pl-9" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="bg-[#0F1424] border-white/10 h-11 text-white w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.industry} onValueChange={(v) => setFilters({ ...filters, industry: v })}>
            <SelectTrigger className="bg-[#0F1424] border-white/10 h-11 text-white w-full sm:w-44"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All industries</SelectItem>{INDUSTRIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.availability} onValueChange={(v) => setFilters({ ...filters, availability: v })}>
            <SelectTrigger className="bg-[#0F1424] border-white/10 h-11 text-white w-full sm:w-44"><SelectValue placeholder="Availability" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any availability</SelectItem>{AVAILABILITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ) : (
        <div className="flex gap-3 mb-4">
          <Input data-testid="talent-search-input" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="e.g. Customer Service Healthcare Salesforce"
            className="bg-[#0F1424] border-white/10 h-11 text-white" />
          <Button onClick={runSearch} disabled={searching} data-testid="talent-search-button"
            className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white h-11 px-6">
            {searching ? <Loader2 className="animate-spin" size={16} /> : <><Search size={16} className="mr-2" /> Search</>}
          </Button>
        </div>
      )}

      {/* List */}
      <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: "#0F1424" }}>
        {loading && view === "all" ? (
          <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="animate-spin mr-2" size={20} /> Loading&hellip;</div>
        ) : list.length === 0 ? (
          <div className="text-center py-16 text-white/50" data-testid="candidates-empty">
            {view === "search" ? "Run a search to find candidates." : "No candidates yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/60">Name</TableHead>
                  <TableHead className="text-white/60 hidden md:table-cell">Position</TableHead>
                  <TableHead className="text-white/60">Status</TableHead>
                  <TableHead className="text-white/60 hidden lg:table-cell">Availability</TableHead>
                  <TableHead className="text-white/60 hidden lg:table-cell">Rating</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((c) => (
                  <TableRow key={c.id} data-testid={`candidate-row-${c.id}`} onClick={() => setOpenId(c.id)}
                    className="border-white/10 cursor-pointer hover:bg-white/5">
                    <TableCell className="font-medium text-white">
                      {c.name}
                      <div className="text-white/40 text-xs">{c.email}</div>
                    </TableCell>
                    <TableCell className="text-white/70 hidden md:table-cell">{c.current_position || c.desired_positions || "\u2014"}</TableCell>
                    <TableCell><Badge className={`${statusColor(c.status)} border rounded-md`}>{c.status}</Badge></TableCell>
                    <TableCell className="text-white/60 hidden lg:table-cell">{c.availability || "\u2014"}</TableCell>
                    <TableCell className="hidden lg:table-cell"><Stars value={c.recruiter_rating || 0} readOnly /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-white/40 text-xs mt-3">{list.length} candidate(s)</p>

      {/* Full Candidate Workspace */}
      {openId && (
        <CandidateWorkspace token={token} id={openId}
          onClose={() => setOpenId(null)} onChanged={load} onDeleted={() => setOpenId(null)} />
      )}

      {/* Candidate Intake (resume upload / manual) */}
      {showIntake && (
        <CandidateIntakeModal token={token}
          onClose={() => setShowIntake(false)}
          onCreated={(id) => { setShowIntake(false); load(); if (id) setOpenId(id); }} />
      )}
    </div>
  );
};

export default CandidatesManager;
